import React, { useState, useEffect } from 'react';
import { Calendar, Phone, Gift } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { trackPhoneCall } from '@/services/menuViewService';
import ReservationModal from '@/components/ReservationModal';
import { WheelOfFortune } from './WheelOfFortune';

interface MenuBottomNavbarProps {
  restaurantId: string;
  primaryColor?: string;
  restaurantName?: string;
}

const MenuBottomNavbar: React.FC<MenuBottomNavbarProps> = ({
  restaurantId,
  primaryColor = '#8B5CF6',
  restaurantName = 'Restaurant',
}) => {
  const [reservationEnabled, setReservationEnabled] = useState(false);
  const [phone, setPhone] = useState('');
  const [actualName, setActualName] = useState(restaurantName);
  const [wheelConfig, setWheelConfig] = useState<any>(null);
  const [showReservation, setShowReservation] = useState(false);
  const [showWheel, setShowWheel] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!restaurantId) return;
    const fetchData = async () => {
      setLoading(true);
      const [restaurantRes, settingsRes, wheelRes] = await Promise.all([
        supabase.from('restaurants').select('phone, name').eq('id', restaurantId).maybeSingle(),
        supabase.from('reservation_settings').select('online_reservations_enabled').eq('restaurant_id', restaurantId).maybeSingle(),
        supabase.from('wheel_configurations').select('id, primary_color').eq('restaurant_id', restaurantId).eq('is_enabled', true).maybeSingle(),
      ]);

      if (restaurantRes.data) {
        setPhone(restaurantRes.data.phone || '');
        setActualName(restaurantRes.data.name || restaurantName);
      }
      setReservationEnabled(settingsRes.data?.online_reservations_enabled ?? false);
      setWheelConfig(wheelRes.data);
      setLoading(false);
    };
    fetchData();
  }, [restaurantId, restaurantName]);

  const handleCall = () => {
    trackPhoneCall(restaurantId).catch(() => {});
    window.location.href = `tel:${phone}`;
  };

  if (loading) return null;

  const hasPhone = phone.trim().length > 0;
  const hasWheel = !!wheelConfig;
  const hasReservation = reservationEnabled;

  // Count active items for grid
  const items: { key: string; icon: React.ReactNode; label: string; onClick: () => void; highlight?: boolean }[] = [];
  
  if (hasReservation) {
    items.push({
      key: 'reserve',
      icon: <Calendar className="h-5 w-5" />,
      label: 'Réserver',
      onClick: () => setShowReservation(true),
      highlight: true,
    });
  }

  if (hasPhone) {
    items.push({
      key: 'call',
      icon: <Phone className="h-5 w-5" />,
      label: 'Appeler',
      onClick: handleCall,
    });
  }

  if (hasWheel) {
    items.push({
      key: 'wheel',
      icon: <Gift className="h-5 w-5" />,
      label: 'Jeu',
      onClick: () => setShowWheel(true),
    });
  }

  if (items.length === 0) return null;

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-background/95 backdrop-blur-md border-t border-border safe-area-bottom">
        <div className="max-w-lg mx-auto flex items-stretch">
          {items.map((item) => (
            <button
              key={item.key}
              onClick={item.onClick}
              className={`flex-1 flex flex-col items-center justify-center gap-1 py-3 px-2 transition-colors ${
                item.highlight
                  ? 'text-white'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              style={item.highlight ? { backgroundColor: primaryColor } : undefined}
            >
              {item.icon}
              <span className="text-[11px] font-medium leading-none">{item.label}</span>
            </button>
          ))}
        </div>
      </div>

      <ReservationModal
        isOpen={showReservation}
        onClose={() => setShowReservation(false)}
        restaurantId={restaurantId}
        restaurantName={actualName}
      />

      {showWheel && (
        <WheelOfFortune
          restaurantId={restaurantId}
          onClose={() => setShowWheel(false)}
        />
      )}
    </>
  );
};

export default MenuBottomNavbar;
